/**
 * Rule-override request API — companion to migration 099.
 *
 * When a booker's booking is rejected by the 097/098 rule-enforcement
 * trigger, they can submit a formal request here. Tenant admins
 * review at /admin/override-requests and either approve (which
 * inserts the booking under the admin's identity, bypassing rules)
 * or deny with a note.
 */
import { supabase } from "@/lib/supabaseClient";

const T = "lbs_rule_override_requests";

/**
 * Create an override request.
 * @param {object} args
 * @param {string} args.client_id
 * @param {string} args.site_id
 * @param {string} [args.bay_id]
 * @param {string} args.scheduled_start ISO
 * @param {string} args.scheduled_end   ISO
 * @param {string} args.rule_error      the raw trigger message
 * @param {object} args.booking_payload full form values snapshot
 */
export async function createOverrideRequest(args) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: { message: "Not signed in" } };
  const body = {
    client_id: args.client_id,
    site_id: args.site_id,
    bay_id: args.bay_id || null,
    requester_user_id: user.id,
    scheduled_start: args.scheduled_start,
    scheduled_end: args.scheduled_end,
    rule_error: args.rule_error,
    booking_payload: args.booking_payload || {},
  };
  return supabase.from(T).insert(body).select().maybeSingle();
}

export async function listOverrideRequests({ status = null, limit = 50 } = {}) {
  let q = supabase
    .from(T)
    .select(
      `id,client_id,site_id,bay_id,requester_user_id,scheduled_start,scheduled_end,
       rule_error,booking_payload,status,actioned_by_user_id,actioned_at,action_note,
       created_booking_id,created_at,
       requester:lbs_user_profiles!lbs_rule_override_requests_requester_user_id_fkey(id,email,first_name,last_name),
       actioner:lbs_user_profiles!lbs_rule_override_requests_actioned_by_user_id_fkey(id,email,first_name,last_name),
       site:lbs_sites!lbs_rule_override_requests_site_id_fkey(id,name),
       bay:lbs_bays!lbs_rule_override_requests_bay_id_fkey(id,name)`
    )
    .order("created_at", { ascending: false })
    .limit(limit);
  if (status) q = q.eq("status", status);
  return q;
}

/** Admin: approve — creates the booking under the admin's identity. */
export async function approveOverrideRequest(id, note = null) {
  return supabase.rpc("lbs_approve_override_request", {
    p_request_id: id,
    p_note: note,
  });
}

/** Admin: deny with a required reason note. */
export async function denyOverrideRequest(id, note) {
  return supabase.rpc("lbs_deny_override_request", {
    p_request_id: id,
    p_note: note,
  });
}

/** Requester cancels their own pending request. */
export async function cancelOverrideRequest(id) {
  return supabase
    .from(T)
    .update({ status: "cancelled" })
    .eq("id", id)
    .eq("status", "pending")
    .select()
    .maybeSingle();
}
